'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function PregnancyConceptionCalculator() {
  const t = useTranslations("tools.pregnancyConceptionCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [calcMethod, setCalcMethod] = useState('due-date');
  const [inputs, setInputs] = useState({
    dueDate: '',
    lastPeriod: '',
    cycleLength: '28',
    ultrasoundDate: '',
    ultrasoundWeeks: '',
    ultrasoundDays: '',
  });
  const [results, setResults] = useState<{
    conception: Date;
    start: Date;
    end: Date;
  } | null>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ec4899',
    primaryHover: '#f43f5e',
    secondary: '#f59e0b',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(236, 72, 153, 0.2)',
    pink50: '#fef3f2',
    pink100: '#fce7f3',
    blue50: '#eff6ff',
    blue700: '#1e40af',
    yellow50: '#fef3c7',
    yellow800: '#92400e',
    orange50: '#fff7ed',
  };

  // Calculation functions
  const addDays = (date: Date, days: number): Date => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  };

  const formatDate = (date: Date): string => {
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  const calculateFromDueDate = (dueDate: string) => {
    const due = new Date(dueDate);
    const conceptionDate = addDays(due, -266);
    const windowStart = addDays(conceptionDate, -5);
    const windowEnd = addDays(conceptionDate, 1);

    return {
      conception: conceptionDate,
      start: windowStart,
      end: windowEnd,
    };
  };

  const calculateFromLastPeriod = (lastPeriod: string, cycleLength: string) => {
    const lmp = new Date(lastPeriod);
    const ovulationDay = parseInt(cycleLength) - 14;
    const conceptionDate = addDays(lmp, ovulationDay);
    const windowStart = addDays(conceptionDate, -5);
    const windowEnd = addDays(conceptionDate, 1);

    return {
      conception: conceptionDate,
      start: windowStart,
      end: windowEnd,
    };
  };

  const calculateFromUltrasound = (ultrasoundDate: string, weeks: string, days: string) => {
    const usDate = new Date(ultrasoundDate);
    const totalDays = (parseInt(weeks) * 7) + parseInt(days);
    const conceptionDate = addDays(usDate, -(totalDays - 14));
    const windowStart = addDays(conceptionDate, -5);
    const windowEnd = addDays(conceptionDate, 1);

    return {
      conception: conceptionDate,
      start: windowStart,
      end: windowEnd,
    };
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    let result;

    if (calcMethod === 'due-date' && inputs.dueDate) {
      result = calculateFromDueDate(inputs.dueDate);
    } else if (calcMethod === 'last-period' && inputs.lastPeriod) {
      result = calculateFromLastPeriod(inputs.lastPeriod, inputs.cycleLength);
    } else if (calcMethod === 'ultrasound' && inputs.ultrasoundDate && inputs.ultrasoundWeeks && inputs.ultrasoundDays) {
      result = calculateFromUltrasound(inputs.ultrasoundDate, inputs.ultrasoundWeeks, inputs.ultrasoundDays);
    }

    if (result) {
      setResults(result);
      setTimeout(() => {
        document.getElementById('results-section')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  // Get max dates
  const today = new Date().toISOString().split('T')[0];
  const futureDate = new Date();
  futureDate.setFullYear(futureDate.getFullYear() + 1);
  const maxFutureDate = futureDate.toISOString().split('T')[0];

  // Styles
  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, margin: '0 auto' },
    { fontSize: '1rem', maxWidth: '100%' },
    { fontSize: '1.125rem', maxWidth: '700px' },
    { fontSize: '1.25rem', maxWidth: '800px' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: `linear-gradient(135deg, ${colors.pink50} 0%, ${colors.pink100} 100%)`,
      borderRadius: '1rem',
      marginBottom: '2rem',
      overflow: 'hidden',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { margin: '0 auto', background: 'white', borderRadius: '1rem', boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)', overflow: 'hidden' },
    { maxWidth: '100%' },
    { maxWidth: '600px' },
    { maxWidth: '800px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    {
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryHover} 100%)`,
      color: 'white',
      textAlign: 'center' as const,
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const toolHeaderH2Style = getResponsiveStyle(
    { margin: 0, fontWeight: 700 },
    { fontSize: '1.5rem' },
    { fontSize: '1.65rem' },
    { fontSize: '1.8rem' }
  );

  const formStyle = getResponsiveStyle(
    {},
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const formGroupStyle = getResponsiveStyle(
    {},
    { marginBottom: '1.25rem' },
    { marginBottom: '1.375rem' },
    { marginBottom: '1.5rem' }
  );

  const labelStyle = {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: 600,
    color: colors.textPrimary,
    fontSize: '0.95rem',
  };

  const inputBaseStyle = {
    width: '100%',
    padding: '0.75rem',
    border: '2px solid #e5e7eb',
    borderRadius: '0.5rem',
    fontSize: '1rem',
    transition: 'all 0.3s ease',
    boxSizing: 'border-box' as const,
  };

  const calculateBtnStyle = getResponsiveStyle(
    {
      width: '100%',
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryHover} 100%)`,
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '1rem',
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.0625rem' },
    { padding: '1.125rem 2.5rem', fontSize: '1.125rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: `linear-gradient(135deg, ${colors.pink50} 0%, ${colors.pink100} 100%)`,
      borderTop: `3px solid ${colors.primary}`,
      display: results ? 'block' : 'none',
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { margin: '0 0 1.5rem 0', color: colors.primary, textAlign: 'center' as const },
    { fontSize: '1.35rem' },
    { fontSize: '1.425rem' },
    { fontSize: '1.5rem' }
  );

  const resultCardStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '0.75rem',
      marginBottom: '1rem',
      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
    },
    { padding: '1.25rem' },
    { padding: '1.375rem' },
    { padding: '1.5rem' }
  );

  const resultItemStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.75rem 0',
    borderBottom: '1px solid #f3f4f6',
  };

  const resultLabelStyle = {
    fontWeight: 600,
    color: '#6b7280',
    fontSize: '0.95rem',
  };

  const resultValueStyle = getResponsiveStyle(
    { fontWeight: 700, color: colors.primary, textAlign: 'right' as const },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const conceptionWindowStyle = getResponsiveStyle(
    {
      background: `linear-gradient(135deg, ${colors.pink50} 0%, ${colors.orange50} 100%)`,
      borderRadius: '0.75rem',
      marginTop: '1.5rem',
      borderLeft: `4px solid ${colors.secondary}`,
    },
    { padding: '1.25rem' },
    { padding: '1.375rem' },
    { padding: '1.5rem' }
  );

  const conceptionWindowH4Style = {
    margin: '0 0 1rem 0',
    color: colors.secondary,
    fontSize: '1.1rem',
  };

  const conceptionWindowPStyle = {
    margin: '0.5rem 0',
    color: colors.textSecondary,
    lineHeight: 1.6,
  };

  const dateHighlightStyle = {
    background: colors.yellow50,
    padding: '0.25rem 0.5rem',
    borderRadius: '0.25rem',
    fontWeight: 600,
    color: colors.yellow800,
  };

  const infoBoxStyle = getResponsiveStyle(
    {
      background: colors.blue50,
      borderLeft: `4px solid #2563eb`,
      borderRadius: '0.5rem',
      marginTop: '1rem',
    },
    { padding: '0.875rem' },
    { padding: '0.9375rem' },
    { padding: '1rem' }
  );

  const infoBoxPStyle = {
    margin: 0,
    color: colors.blue700,
    fontSize: '0.9rem',
    lineHeight: 1.5,
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(236, 72, 153, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem', marginTop: 0 },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem', marginBottom: '1.25rem' },
    { fontSize: '2rem', marginBottom: '1.5rem' }
  );

  const h3Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.75rem', marginTop: '1.5rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.7, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.0625rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = {
    paddingLeft: '1.5rem',
    marginBottom: '1rem',
  };

  const listItemStyle = getResponsiveStyle(
    { marginBottom: '0.75rem', lineHeight: 1.6, color: colors.textSecondary },
    { fontSize: '1rem' },
    { fontSize: '1.0625rem' },
    { fontSize: '1.125rem' }
  );

  const strongStyle = {
    fontWeight: 600,
    color: colors.textPrimary,
  };

  const inlineGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  return (
    <div style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <h1 style={h1Style}>{t("title")}</h1>
        <p style={summaryTextStyle}>{t("subtitle")}</p>
      </header>

      {/* Calculator Tool */}
      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolHeaderH2Style}>{t("toolTitle")}</h2>
          </div>

          <form style={formStyle} onSubmit={handleCalculate}>
            <div style={formGroupStyle}>
              <label htmlFor="calc-method" style={labelStyle}>
                {t("calcMethodLabel")}
              </label>
              <select
                id="calc-method"
                style={inputBaseStyle}
                value={calcMethod}
                onChange={(e) => setCalcMethod(e.target.value)}
                required
              >
                <option value="due-date">{t("dueDateOption")}</option>
                <option value="last-period">{t("lastPeriodOption")}</option>
                <option value="ultrasound">{t("ultrasoundOption")}</option>
              </select>
            </div>

            {calcMethod === 'due-date' && (
              <div style={formGroupStyle}>
                <label htmlFor="due-date" style={labelStyle}>
                  {t("dueDateLabel")}
                </label>
                <input
                  type="date"
                  id="due-date"
                  style={inputBaseStyle}
                  value={inputs.dueDate}
                  onChange={(e) => handleInputChange('dueDate', e.target.value)}
                  max={maxFutureDate}
                  required
                />
              </div>
            )}

            {calcMethod === 'last-period' && (
              <>
                <div style={formGroupStyle}>
                  <label htmlFor="last-period" style={labelStyle}>
                    {t("lastPeriodLabel")}
                  </label>
                  <input
                    type="date"
                    id="last-period"
                    style={inputBaseStyle}
                    value={inputs.lastPeriod}
                    onChange={(e) => handleInputChange('lastPeriod', e.target.value)}
                    max={today}
                    required
                  />
                </div>
                <div style={formGroupStyle}>
                  <label htmlFor="cycle-length" style={labelStyle}>
                    {t("cycleLengthLabel")}
                  </label>
                  <select
                    id="cycle-length"
                    style={inputBaseStyle}
                    value={inputs.cycleLength}
                    onChange={(e) => handleInputChange('cycleLength', e.target.value)}
                  >
                    {[22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35].map(days => (
                      <option key={days} value={days}>{days} {t("daysUnit")}</option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {calcMethod === 'ultrasound' && (
              <>
                <div style={formGroupStyle}>
                  <label htmlFor="ultrasound-date" style={labelStyle}>
                    {t("ultrasoundDateLabel")}
                  </label>
                  <input
                    type="date"
                    id="ultrasound-date"
                    style={inputBaseStyle}
                    value={inputs.ultrasoundDate}
                    onChange={(e) => handleInputChange('ultrasoundDate', e.target.value)}
                    max={today}
                    required
                  />
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>
                    {t("pregnancyLengthLabel")}
                  </label>
                  <div style={inlineGridStyle}>
                    <input
                      type="number"
                      id="ultrasound-weeks"
                      style={inputBaseStyle}
                      placeholder={t("weeksPlaceholder")}
                      value={inputs.ultrasoundWeeks}
                      onChange={(e) => handleInputChange('ultrasoundWeeks', e.target.value)}
                      min="0"
                      max="42"
                      required
                    />
                    <input
                      type="number"
                      id="ultrasound-days"
                      style={inputBaseStyle}
                      placeholder={t("daysPlaceholder")}
                      value={inputs.ultrasoundDays}
                      onChange={(e) => handleInputChange('ultrasoundDays', e.target.value)}
                      min="0"
                      max="6"
                      required
                    />
                  </div>
                </div>
              </>
            )}

            <button type="submit" style={calculateBtnStyle}>
              {t("calculateButton")}
            </button>
          </form>

          {results && (
            <div style={resultsStyle} id="results-section">
              <h3 style={resultsH3Style}>{t("resultsTitle")}</h3>

              <div style={resultCardStyle}>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t("conceptionDateLabel")}</span>
                  <span style={resultValueStyle}>{formatDate(results.conception)}</span>
                </div>
                <div style={{ ...resultItemStyle, borderBottom: 'none' }}>
                  <span style={resultLabelStyle}>{t("conceptionRangeLabel")}</span>
                  <span style={resultValueStyle}>
                    {formatDate(results.start)} - {formatDate(results.end)}
                  </span>
                </div>
              </div>

              <div style={conceptionWindowStyle}>
                <h4 style={conceptionWindowH4Style}>{t("windowDetailsTitle")}</h4>
                <p style={conceptionWindowPStyle}>
                  {t("windowDetailsPart1")}{' '}
                  <span style={dateHighlightStyle}>{formatDate(results.conception)}</span>.
                </p>
                <p style={conceptionWindowPStyle}>
                  {t("windowDetailsPart2")}{' '}
                  <span style={dateHighlightStyle}>{formatDate(results.start)}</span>{' '}
                  {t("windowDetailsPart3")}{' '}
                  <span style={dateHighlightStyle}>{formatDate(results.end)}</span>.
                </p>
                <p style={conceptionWindowPStyle}>
                  {t("windowDetailsPart4")}
                </p>
              </div>

              <div style={infoBoxStyle}>
                <p style={infoBoxPStyle}>
                  <strong>{t("noteLabel")}</strong> {t("noteText")}
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Intro Section */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("introTitle")}</h2>
        <p style={paragraphStyle}>{t("introPara1")}</p>
        <p style={paragraphStyle}>{t("introPara2")}</p>
        <p style={paragraphStyle}>{t("introPara3")}</p>
      </section>

      {/* How to Use */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("howToUseTitle")}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t("step1Title")}</strong> {t("step1Text")}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t("step2Title")}</strong> {t("step2Text")}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t("step3Title")}</strong> {t("step3Text")}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t("step4Title")}</strong> {t("step4Text")}
          </li>
        </ol>
      </section>

      {/* Calculation Methods */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("methodsTitle")}</h2>

        <h3 style={h3Style}>{t("method1Title")}</h3>
        <p style={paragraphStyle}>{t("method1Text")}</p>

        <h3 style={h3Style}>{t("method2Title")}</h3>
        <p style={paragraphStyle}>{t("method2Text")}</p>

        <h3 style={h3Style}>{t("method3Title")}</h3>
        <p style={paragraphStyle}>{t("method3Text")}</p>
      </section>

      {/* Understanding Results */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("understandingTitle")}</h2>
        <p style={paragraphStyle}>{t("understandingPara1")}</p>
        <p style={paragraphStyle}>{t("understandingPara2")}</p>
        <p style={paragraphStyle}>{t("understandingPara3")}</p>
      </section>

      {/* Important Considerations */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("considerationsTitle")}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t("consideration1")}</li>
          <li style={listItemStyle}>{t("consideration2")}</li>
          <li style={listItemStyle}>{t("consideration3")}</li>
          <li style={listItemStyle}>{t("consideration4")}</li>
          <li style={listItemStyle}>{t("consideration5")}</li>
        </ul>
      </section>

      {/* FAQ */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("faqTitle")}</h2>

        <h3 style={h3Style}>{t("faq1Question")}</h3>
        <p style={paragraphStyle}>{t("faq1Answer")}</p>

        <h3 style={h3Style}>{t("faq2Question")}</h3>
        <p style={paragraphStyle}>{t("faq2Answer")}</p>

        <h3 style={h3Style}>{t("faq3Question")}</h3>
        <p style={paragraphStyle}>{t("faq3Answer")}</p>

        <h3 style={h3Style}>{t("faq4Question")}</h3>
        <p style={paragraphStyle}>{t("faq4Answer")}</p>

        <h3 style={h3Style}>{t("faq5Question")}</h3>
        <p style={paragraphStyle}>{t("faq5Answer")}</p>
      </section>

      {/* Clinical Guidance */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("clinicalTitle")}</h2>
        <p style={paragraphStyle}>{t("clinicalPara1")}</p>
        <p style={paragraphStyle}>{t("clinicalPara2")}</p>
        <p style={paragraphStyle}>{t("clinicalPara3")}</p>
      </section>

      {/* References */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("referencesTitle")}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t("reference1")}</li>
          <li style={listItemStyle}>{t("reference2")}</li>
          <li style={listItemStyle}>{t("reference3")}</li>
          <li style={listItemStyle}>{t("reference4")}</li>
        </ul>
      </section>
    </div>
  );
}